import { useMemo, useState } from "react";
import { ig } from "../inbox/_inbox_shared";
import {
  X, Save, Trash2, ChevronsRight, Maximize2, Info, Mail, MessageSquare,
  Tag, Code2, Undo2, Redo2, Bold, Italic, Underline, Strikethrough,
  AlignLeft, List, Link as LinkIcon, Image as ImageIcon, Sparkles,
  CheckCircle2, MailOpen, MousePointerClick, MessageCircle, AlertOctagon,
  UserMinus, ShieldCheck, ShieldX, Flag, Send, Phone, Paperclip,
  Plus, ChevronDown, ChevronRight,
} from "lucide-react";

/**
 * Node configuration drawer.
 *
 *  • send_email  → rich Email editor + Statistics tab
 *  • send_sms    → SMS editor + Statistics tab
 *  • everything else → generic field editor (legacy FIELDS_BY_TYPE)
 */
export default function NodeConfigDrawer({ node, onClose, onSave, onDelete }) {
  if (node.type === "send_email") {
    return <EmailDrawer node={node} onClose={onClose} onSave={onSave} onDelete={onDelete} />;
  }
  if (node.type === "send_sms") {
    return <SmsDrawer node={node} onClose={onClose} onSave={onSave} onDelete={onDelete} />;
  }
  return <GenericDrawer node={node} onClose={onClose} onSave={onSave} onDelete={onDelete} />;
}

/* ─────────────────────────────────────────────────────────────
   Shared shell — header, tabs, sticky footer
   ───────────────────────────────────────────────────────────── */
function DrawerShell({ icon: Icon, iconTone, type, title, description, tab, setTab,
                       tabs, onClose, onDelete, onSave, children, testId }) {
  return (
    <div onClick={onClose} data-testid={testId} style={{
      position: "fixed", inset: 0, background: "rgba(7,31,24,0.32)", zIndex: 100000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: 540, background: ig.card,
        boxShadow: "-12px 0 32px rgba(7,31,24,0.18)", display: "flex", flexDirection: "column",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      }}>
        {/* ── Header ── */}
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ig.border}`,
          display: "flex", alignItems: "center", gap: 10 }}>
          <button data-testid="cfg-collapse" style={iconBtnGhost} title="Collapse">
            <ChevronsRight size={14} />
          </button>
          <span style={{
            width: 36, height: 36, borderRadius: 9, display: "grid", placeItems: "center",
            background: iconTone.bg, color: iconTone.fg, flexShrink: 0,
          }}>
            <Icon size={17} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: ig.ink, lineHeight: 1.2 }}>{title}</div>
            <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 2, lineHeight: 1.35 }}>{description}</div>
          </div>
          <button data-testid="cfg-learn-more" style={{
            ...iconBtnGhost, width: "auto", padding: "0 10px", gap: 4, fontSize: 11.5, fontWeight: 700,
          }}>
            <Info size={12} /> Learn more
          </button>
          <button data-testid="cfg-expand" style={iconBtnGhost} title="Expand">
            <Maximize2 size={13} />
          </button>
          <button data-testid="config-close" onClick={onClose} style={iconBtnGhost} title="Close">
            <X size={14} />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: "flex", borderBottom: `1px solid ${ig.border}`, padding: "0 16px", background: ig.card }}>
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`cfg-tab-${k}`} onClick={() => setTab(k)} style={{
              padding: "12px 4px", marginRight: 22, border: 0, background: "transparent", cursor: "pointer",
              color: tab === k ? "#2563EB" : ig.muted, fontWeight: 700, fontSize: 13,
              borderBottom: "2px solid " + (tab === k ? "#2563EB" : "transparent"),
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Scroll body ── */}
        <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", background: "#FAFCFB" }}>
          {children}
        </div>

        {/* ── Sticky footer ── */}
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${ig.border}`,
          display: "flex", alignItems: "center", gap: 8, background: ig.card }}>
          <button data-testid="cfg-delete" onClick={onDelete} style={{
            ...btnSecondary, color: "#B91C1C", borderColor: "#FECACA", display: "inline-flex",
            alignItems: "center", gap: 6,
          }}>
            <Trash2 size={13} /> Delete
          </button>
          <div style={{ flex: 1 }} />
          <button data-testid="cfg-cancel" onClick={onClose} style={btnSecondary}>Cancel</button>
          <button data-testid="cfg-save" onClick={onSave} style={btnPrimary}>
            <Save size={13} /> Save action
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   EMAIL
   ───────────────────────────────────────────────────────────── */
function EmailDrawer({ node, onClose, onSave, onDelete }) {
  const c0 = node.config || {};
  const [tab, setTab] = useState("edit");
  const [actionName, setActionName] = useState(node.label || "Conversational Email");
  const [fromName, setFromName]     = useState(c0.from_name || "");
  const [fromEmail, setFromEmail]   = useState(c0.from_email || "");
  const [cc, setCc]                 = useState(c0.cc || "");
  const [bcc, setBcc]               = useState(c0.bcc || "");
  const [showCc, setShowCc]         = useState(!!c0.cc);
  const [showBcc, setShowBcc]       = useState(!!c0.bcc);
  const [subject, setSubject]       = useState(c0.subject || "");
  const [preheader, setPreheader]   = useState(c0.preheader || "");
  const [mode, setMode]             = useState(c0.compose_mode || "quick");
  const [body, setBody]             = useState(c0.body || "");
  const [extraOpen, setExtraOpen]   = useState(false);
  const [trackClicks, setTrackClicks]   = useState(c0.track_clicks ?? true);
  const [utmTracking, setUtmTracking]   = useState(c0.utm_tracking ?? false);
  const [addTags, setAddTags]           = useState(c0.add_tags || "");

  const charCount = body.length;
  const wordCount = useMemo(
    () => (body.trim() ? body.trim().split(/\s+/).length : 0),
    [body]
  );

  const save = () => onSave({
    label: actionName || "Conversational Email",
    summary: subject ? `Subject: ${subject}` : "Send an email to the contact",
    config: {
      ...c0,
      from_name: fromName, from_email: fromEmail,
      cc, bcc, subject, preheader,
      compose_mode: mode, body,
      track_clicks: trackClicks, utm_tracking: utmTracking, add_tags: addTags,
    },
  });

  return (
    <DrawerShell
      testId="email-config"
      icon={Mail}
      iconTone={{ bg: "#DBEAFE", fg: "#1E40AF" }}
      type="email"
      title="Email"
      description="Send an email to the contact"
      tab={tab} setTab={setTab}
      tabs={[["edit", "Edit action"], ["stats", "Statistics"]]}
      onClose={onClose} onDelete={onDelete} onSave={save}
    >
      {tab === "edit" ? (
        <div style={{ padding: 18 }}>
          <Card>
            <FieldRow label="Action Name" required>
              <input data-testid="email-action-name" value={actionName}
                onChange={(e) => setActionName(e.target.value)} style={input} />
            </FieldRow>
            <Row2>
              <FieldRow label="From Name">
                <input data-testid="email-from-name" value={fromName}
                  onChange={(e) => setFromName(e.target.value)} placeholder="From name" style={input} />
              </FieldRow>
              <FieldRow label="From Email">
                <input data-testid="email-from-email" value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)} placeholder="From email" style={input} />
              </FieldRow>
            </Row2>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button data-testid="email-add-cc" onClick={() => setShowCc(true)} style={chipBtn}>
                <Plus size={11} /> CC
              </button>
              <button data-testid="email-add-bcc" onClick={() => setShowBcc(true)} style={chipBtn}>
                <Plus size={11} /> BCC
              </button>
            </div>
            {showCc && (
              <FieldRow label="CC">
                <input data-testid="email-cc" value={cc} onChange={(e) => setCc(e.target.value)}
                  placeholder="cc@example.com" style={input} />
              </FieldRow>
            )}
            {showBcc && (
              <FieldRow label="BCC">
                <input data-testid="email-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)}
                  placeholder="bcc@example.com" style={input} />
              </FieldRow>
            )}
            <FieldRow label="Subject" required>
              <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Hi {{contact.first_name}} — {{custom_values.promotion_name}}" style={input} />
              <MergeHint />
            </FieldRow>
            <FieldRow label="Pre-Header / Preview Text">
              <input data-testid="email-preheader" value={preheader} onChange={(e) => setPreheader(e.target.value)}
                placeholder="Optional preview shown next to the subject in inbox" style={input} />
            </FieldRow>
          </Card>

          {/* Create Email options */}
          <SectionTitle>Create Email</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              ["quick",    "Quick compose",          "Plain text composer"],
              ["smart",    "From smart builder",     "Drag-and-drop builder"],
              ["template", "Select existing template", "Pick a saved template"],
            ].map(([k, t, sub]) => (
              <button key={k} data-testid={`email-mode-${k}`} onClick={() => setMode(k)} style={{
                ...modeCard,
                borderColor: mode === k ? "#2563EB" : ig.border,
                background: mode === k ? "#EFF6FF" : ig.card,
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: mode === k ? "#1D4ED8" : ig.ink }}>{t}</div>
                <div style={{ fontSize: 11, color: ig.muted, marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
              </button>
            ))}
          </div>

          {/* Editor */}
          <SectionTitle>Message</SectionTitle>
          <Card padding={0}>
            <EditorToolbar />
            <textarea data-testid="email-body" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{contact.first_name}},

Thanks for reaching out about {{custom_values.promotion_name}}. Quick question to help you faster…"
              rows={11}
              style={{
                width: "100%", border: 0, outline: "none", padding: "14px 16px", fontSize: 13.2,
                lineHeight: 1.6, fontFamily: "inherit", background: ig.card, color: ig.ink, resize: "vertical",
              }} />
            <div style={{
              display: "flex", alignItems: "center", borderTop: `1px solid ${ig.borderSoft}`,
              padding: "8px 14px", background: "#FAFCFB", gap: 10,
            }}>
              <button data-testid="email-write-ai" style={aiBtn}>
                <Sparkles size={12} /> Write with AI
              </button>
              <div style={{ flex: 1 }} />
              <span style={meta} data-testid="email-chars">{charCount} chars</span>
              <span style={dot} />
              <span style={meta} data-testid="email-words">{wordCount} words</span>
            </div>
          </Card>

          {/* Additional settings accordion */}
          <Card padding={0} style={{ marginTop: 14 }}>
            <button data-testid="email-additional-toggle" onClick={() => setExtraOpen((v) => !v)} style={{
              width: "100%", display: "flex", alignItems: "center", padding: "14px 16px",
              border: 0, background: "transparent", cursor: "pointer", color: ig.ink,
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, flex: 1, textAlign: "left" }}>Additional settings</span>
              {extraOpen ? <ChevronDown size={14} color={ig.muted} /> : <ChevronRight size={14} color={ig.muted} />}
            </button>
            {extraOpen && (
              <div style={{ borderTop: `1px solid ${ig.borderSoft}`, padding: 16 }}>
                <Toggle testId="email-track-clicks" checked={trackClicks} onChange={setTrackClicks}
                  title="Track clicks" hint="Replace links with tracked redirects." />
                <Toggle testId="email-utm" checked={utmTracking} onChange={setUtmTracking}
                  title="UTM tracking" hint="Append UTM parameters to outbound links." />
                <FieldRow label="Add tags on send">
                  <input data-testid="email-add-tags" value={addTags}
                    onChange={(e) => setAddTags(e.target.value)} placeholder="email-sent, nurture-week-1" style={input} />
                </FieldRow>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <EmailStatsTab />
      )}
    </DrawerShell>
  );
}

function EditorToolbar() {
  const tools = [
    { Icon: Undo2 }, { Icon: Redo2 },
    { Icon: Bold }, { Icon: Italic }, { Icon: Underline }, { Icon: Strikethrough },
  ];
  return (
    <div style={{
      display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6,
      padding: "8px 12px", borderBottom: `1px solid ${ig.borderSoft}`, background: "#FAFCFB",
    }}>
      <button data-testid="email-merge-tag" style={miniTool} title="Merge tag">
        <Tag size={12} />
      </button>
      <button style={miniTool} title="HTML"><Code2 size={12} /></button>
      <Sep />
      {tools.map(({ Icon }, i) => (
        <button key={i} style={miniTool}><Icon size={12} /></button>
      ))}
      <Sep />
      <select style={selectMini} defaultValue="default" data-testid="email-font">
        <option value="default">Default font</option>
        <option value="inter">Inter</option>
        <option value="georgia">Georgia</option>
      </select>
      <select style={selectMini} defaultValue="14" data-testid="email-font-size">
        <option value="12">12</option><option value="14">14</option>
        <option value="16">16</option><option value="18">18</option>
      </select>
      <select style={selectMini} defaultValue="p" data-testid="email-paragraph">
        <option value="p">Paragraph</option><option value="h1">H1</option><option value="h2">H2</option>
      </select>
      <select style={selectMini} defaultValue="1.6" data-testid="email-line-height">
        <option value="1.4">1.4</option><option value="1.6">1.6</option><option value="1.8">1.8</option>
      </select>
      <Sep />
      {[AlignLeft, List, LinkIcon, ImageIcon, Code2].map((Icon, i) => (
        <button key={i} style={miniTool}><Icon size={12} /></button>
      ))}
    </div>
  );
}

function EmailStatsTab() {
  const cards = [
    { k: "total",        label: "Total",         Icon: Mail,                tone: { bg: "#DBEAFE", fg: "#1E40AF" } },
    { k: "delivered",    label: "Delivered",     Icon: CheckCircle2,        tone: { bg: "#DCFCE7", fg: "#15803D" } },
    { k: "opened",       label: "Opened",        Icon: MailOpen,            tone: { bg: "#DBEAFE", fg: "#1E40AF" } },
    { k: "clicked",      label: "Clicked",       Icon: MousePointerClick,   tone: { bg: "#EDE9FE", fg: "#6D28D9" } },
    { k: "replied",      label: "Replied",       Icon: MessageCircle,       tone: { bg: "#DCFCE7", fg: "#15803D" } },
    { k: "bounced",      label: "Bounced",       Icon: AlertOctagon,        tone: { bg: "#FEF3C7", fg: "#92400E" } },
    { k: "unsubscribed", label: "Unsubscribed",  Icon: UserMinus,           tone: { bg: "#FECACA", fg: "#991B1B" } },
    { k: "accepted",     label: "Accepted",      Icon: ShieldCheck,         tone: { bg: "#DCFCE7", fg: "#15803D" } },
    { k: "rejected",     label: "Rejected",      Icon: ShieldX,             tone: { bg: "#FECACA", fg: "#991B1B" } },
    { k: "complained",   label: "Complained",    Icon: Flag,                tone: { bg: "#FEF3C7", fg: "#92400E" } },
  ];
  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontSize: 12.5, color: ig.muted, marginBottom: 12, lineHeight: 1.55 }}>
        Live email delivery analytics for this step. Metrics begin populating once your sending
        provider is connected and the workflow has been published.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {cards.map((c) => (
          <StatCard key={c.k} testId={`email-stat-${c.k}`} Icon={c.Icon} tone={c.tone}
            label={c.label} value="0" pct="0.00%" />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SMS
   ───────────────────────────────────────────────────────────── */
function SmsDrawer({ node, onClose, onSave, onDelete }) {
  const c0 = node.config || {};
  const [tab, setTab] = useState("edit");
  const [actionName, setActionName] = useState(node.label || "Conversational SMS");
  const [template, setTemplate]     = useState(c0.template || "");
  const [body, setBody]             = useState(c0.body || "");
  const [attachments, setAttachments] = useState(c0.attachments || []);
  const [urlInput, setUrlInput]     = useState("");
  const [testPhone, setTestPhone]   = useState(c0.test_phone || "");

  const charCount = body.length;
  const wordCount = useMemo(
    () => (body.trim() ? body.trim().split(/\s+/).length : 0),
    [body]
  );

  const addUrl = () => {
    if (!urlInput.trim()) return;
    setAttachments((a) => [...a, urlInput.trim()]);
    setUrlInput("");
  };

  const save = () => onSave({
    label: actionName || "Conversational SMS",
    summary: body ? body.slice(0, 80) : "Send a text message to the contact",
    config: { ...c0, template, body, attachments, test_phone: testPhone },
  });

  return (
    <DrawerShell
      testId="sms-config"
      icon={MessageSquare}
      iconTone={{ bg: "#DCFCE7", fg: "#15803D" }}
      type="sms"
      title="SMS"
      description="Send a text message to the contact"
      tab={tab} setTab={setTab}
      tabs={[["edit", "Edit action"], ["stats", "Statistics"]]}
      onClose={onClose} onDelete={onDelete} onSave={save}
    >
      {tab === "edit" ? (
        <div style={{ padding: 18 }}>
          <Card>
            <FieldRow label="Action Name" required>
              <input data-testid="sms-action-name" value={actionName}
                onChange={(e) => setActionName(e.target.value)} style={input} />
            </FieldRow>
            <FieldRow label="Templates">
              <select data-testid="sms-template" value={template}
                onChange={(e) => setTemplate(e.target.value)} style={input}>
                <option value="">Select template</option>
                <option value="welcome">Welcome — Funding Lead</option>
                <option value="missed-call">Missed-call text back</option>
                <option value="kreyol-followup">Kreyòl follow-up</option>
                <option value="appt-reminder">Appointment reminder</option>
              </select>
            </FieldRow>
          </Card>

          <SectionTitle>Message</SectionTitle>
          <Card padding={0}>
            <div style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6,
              padding: "8px 12px", borderBottom: `1px solid ${ig.borderSoft}`, background: "#FAFCFB",
            }}>
              <button data-testid="sms-merge-tag" style={miniTool} title="Merge tag"><Tag size={12} /></button>
              <Sep />
              <span style={{ fontSize: 11.5, color: ig.muted }}>
                Merge variables stay literal (e.g. <code style={{ background: "#EEF2F0", padding: "1px 5px", borderRadius: 4 }}>{"{{contact.first_name}}"}</code>)
              </span>
            </div>
            <textarea data-testid="sms-body" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{contact.first_name}}, this is {{user.name}} — quick question about {{custom_values.promotion_name}}. Reply YES to continue."
              rows={6}
              style={{
                width: "100%", border: 0, outline: "none", padding: "14px 16px", fontSize: 13.2,
                lineHeight: 1.6, fontFamily: "inherit", background: ig.card, color: ig.ink, resize: "vertical",
              }} />
            <div style={{
              display: "flex", alignItems: "center", borderTop: `1px solid ${ig.borderSoft}`,
              padding: "8px 14px", background: "#FAFCFB", gap: 10,
            }}>
              <button data-testid="sms-write-ai" style={aiBtn}>
                <Sparkles size={12} /> Write with AI
              </button>
              <div style={{ flex: 1 }} />
              <span style={meta} data-testid="sms-chars">{charCount} chars</span>
              <span style={dot} />
              <span style={meta} data-testid="sms-words">{wordCount} words</span>
            </div>
          </Card>

          {/* Attachments */}
          <SectionTitle>Attachments</SectionTitle>
          <Card>
            <button data-testid="sms-add-attachment" style={{
              ...btnSecondary, width: "100%", display: "inline-flex", alignItems: "center",
              justifyContent: "center", gap: 6,
            }}>
              <Paperclip size={13} /> Add attachments
            </button>
            <div style={{ marginTop: 12, fontSize: 11, fontWeight: 700, color: ig.muted,
              textTransform: "uppercase", letterSpacing: 0.4 }}>
              Or add files through URL
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <input data-testid="sms-url-input" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/file.pdf" style={{ ...input, flex: 1 }} />
              <button data-testid="sms-url-add" onClick={addUrl} style={{
                ...btnPrimary, padding: "8px 16px", minWidth: 64,
              }}>+ Add</button>
            </div>
            {attachments.length > 0 && (
              <div data-testid="sms-attachment-list" style={{ marginTop: 10, display: "flex",
                flexDirection: "column", gap: 6 }}>
                {attachments.map((u, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                    border: `1px solid ${ig.borderSoft}`, borderRadius: 7, background: "#FAFCFB",
                    fontSize: 12, color: ig.ink,
                  }}>
                    <Paperclip size={12} color={ig.muted} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u}</span>
                    <button onClick={() => setAttachments((a) => a.filter((_, idx) => idx !== i))}
                      style={{ ...iconBtnGhost, width: 22, height: 22 }}><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Test SMS */}
          <SectionTitle>Send a test</SectionTitle>
          <Card>
            <FieldRow label="Test phone number">
              <input data-testid="sms-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+1 555 123 4567" style={input} />
            </FieldRow>
            <button data-testid="sms-send-test" style={{
              ...btnPrimary, width: "100%", justifyContent: "center", marginTop: 4,
            }}>
              <Send size={12} /> Send test SMS
            </button>
          </Card>
        </div>
      ) : (
        <SmsStatsTab />
      )}
    </DrawerShell>
  );
}

function SmsStatsTab() {
  const cards = [
    { k: "delivered", label: "Delivered", Icon: CheckCircle2,      tone: { bg: "#DCFCE7", fg: "#15803D" } },
    { k: "clicked",   label: "Clicked",   Icon: MousePointerClick, tone: { bg: "#EDE9FE", fg: "#6D28D9" } },
    { k: "failed",    label: "Failed",    Icon: AlertOctagon,      tone: { bg: "#FECACA", fg: "#991B1B" } },
    { k: "opted_out", label: "Opted Out", Icon: UserMinus,         tone: { bg: "#FEF3C7", fg: "#92400E" } },
  ];
  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontSize: 12.5, color: ig.muted, marginBottom: 12, lineHeight: 1.55 }}>
        Live SMS delivery analytics for this step. Metrics begin populating once Twilio
        (or your preferred carrier) is connected and the workflow has been published.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {cards.map((c) => (
          <StatCard key={c.k} testId={`sms-stat-${c.k}`} Icon={c.Icon} tone={c.tone}
            label={c.label} value="0" pct="0%" />
        ))}
      </div>
      <div style={{ marginTop: 14, padding: 12, background: "#EFF6FF",
        border: "1px solid #BFDBFE", borderRadius: 10, fontSize: 11.5, color: "#1E40AF", lineHeight: 1.5 }}>
        <Phone size={12} style={{ verticalAlign: "middle", marginRight: 6 }} />
        Connect Twilio or WhatsApp Cloud API in <strong>Settings → Integrations</strong> to start
        tracking real delivery, click, and opt-out events.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Generic fallback (existing FIELDS_BY_TYPE behavior)
   ───────────────────────────────────────────────────────────── */
function GenericDrawer({ node, onClose, onSave, onDelete }) {
  const [label, setLabel]     = useState(node.label || "");
  const [summary, setSummary] = useState(node.summary || "");
  const [config, setConfig]   = useState({ ...(node.config || {}) });
  const setCfg = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const fields = FIELDS_BY_TYPE[node.type] || FIELDS_BY_TYPE.__default;

  const save = () => onSave({ label, summary, config });

  return (
    <div onClick={onClose} data-testid="node-config" style={{
      position: "fixed", inset: 0, background: "rgba(7,31,24,0.32)", zIndex: 100000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: 460, background: ig.card,
        boxShadow: "-12px 0 32px rgba(7,31,24,0.18)", display: "flex", flexDirection: "column",
        fontFamily: "Inter, sans-serif",
      }}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${ig.border}`, display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, color: ig.muted, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>
              {node.kind === "trigger" ? "Trigger" : "Action"} · {node.type}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{label || node.label}</div>
          </div>
          <button data-testid="config-close" onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 7, border: `1px solid ${ig.border}`,
            background: ig.card, display: "inline-grid", placeItems: "center", cursor: "pointer",
          }}><X size={13} /></button>
        </div>

        <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          <FieldRow label="Step name">
            <input data-testid="cfg-label" value={label} onChange={(e) => setLabel(e.target.value)} style={input} />
          </FieldRow>
          <FieldRow label="Description">
            <textarea data-testid="cfg-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={2}
              style={{ ...input, height: "auto", minHeight: 60, padding: "8px 11px", fontFamily: "inherit" }} />
          </FieldRow>
          <div style={{ marginTop: 6, marginBottom: 8, fontSize: 10, fontWeight: 800,
            letterSpacing: 0.4, textTransform: "uppercase", color: ig.muted }}>Configuration</div>
          {fields.map((f) => (
            <FieldRow key={f.key} label={f.label}>
              {f.type === "select" ? (
                <select data-testid={`cfg-${f.key}`} value={config[f.key] ?? f.default ?? ""} onChange={(e) => setCfg(f.key, e.target.value)} style={input}>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea data-testid={`cfg-${f.key}`} value={config[f.key] ?? ""} onChange={(e) => setCfg(f.key, e.target.value)} rows={3}
                  placeholder={f.placeholder} style={{ ...input, height: "auto", minHeight: 70, padding: "8px 11px", fontFamily: "inherit" }} />
              ) : (
                <input data-testid={`cfg-${f.key}`} value={config[f.key] ?? ""} onChange={(e) => setCfg(f.key, e.target.value)}
                  type={f.type === "number" ? "number" : "text"} placeholder={f.placeholder} style={input} />
              )}
              {f.hint && <div style={{ fontSize: 11, color: ig.muted, marginTop: 4 }}>{f.hint}</div>}
            </FieldRow>
          ))}
        </div>

        <div style={{ padding: "12px 18px", borderTop: `1px solid ${ig.border}`, display: "flex", gap: 8 }}>
          <button data-testid="cfg-delete" onClick={onDelete} style={{
            ...btnSecondary, color: "#B91C1C", borderColor: "#FECACA", display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Trash2 size={13} /> Delete
          </button>
          <div style={{ flex: 1 }} />
          <button data-testid="cfg-cancel" onClick={onClose} style={btnSecondary}>Cancel</button>
          <button data-testid="cfg-save" onClick={save} style={btnPrimary}>
            <Save size={13} /> Save action
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Small building blocks
   ───────────────────────────────────────────────────────────── */
function Card({ children, padding = 16, style }) {
  return (
    <div style={{
      background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12,
      padding, overflow: "hidden", boxShadow: "0 1px 2px rgba(7,31,24,0.04)",
      ...(style || {}),
    }}>
      {children}
    </div>
  );
}
function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}
function FieldRow({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, color: ig.ink, fontWeight: 700, marginBottom: 6 }}>
        {label} {required && <span style={{ color: "#DC2626" }}>*</span>}
      </div>
      {children}
    </div>
  );
}
function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, color: ig.muted, textTransform: "uppercase",
      letterSpacing: 0.6, margin: "18px 0 8px" }}>{children}</div>
  );
}
function MergeHint() {
  return (
    <div style={{ fontSize: 11, color: ig.muted, marginTop: 4 }}>
      Use merge variables like <code style={cd}>{"{{contact.first_name}}"}</code> or <code style={cd}>{"{{custom_values.promotion_name}}"}</code>.
    </div>
  );
}
function Toggle({ testId, checked, onChange, title, hint }) {
  return (
    <label data-testid={testId} style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
      borderBottom: `1px dashed ${ig.borderSoft}`, cursor: "pointer",
    }}>
      <button type="button" onClick={() => onChange(!checked)} style={{
        width: 36, height: 20, borderRadius: 999, border: 0, padding: 0, cursor: "pointer",
        background: checked ? "#22C55E" : "#E5E7EB", position: "relative", flexShrink: 0,
      }}>
        <span style={{
          position: "absolute", top: 2, left: checked ? 18 : 2, width: 16, height: 16,
          borderRadius: 999, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          transition: "left 160ms",
        }} />
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: ig.ink }}>{title}</div>
        {hint && <div style={{ fontSize: 11, color: ig.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </label>
  );
}
function StatCard({ testId, Icon, tone, label, value, pct }) {
  return (
    <div data-testid={testId} style={{
      background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12, padding: 14,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          width: 32, height: 32, borderRadius: 8, display: "grid", placeItems: "center",
          background: tone.bg, color: tone.fg,
        }}><Icon size={15} /></span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: ig.ink }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: ig.ink, letterSpacing: -0.5 }}>{value}</span>
        <span style={{ fontSize: 12, color: ig.muted, fontWeight: 600 }}>{pct}</span>
      </div>
      <button style={{
        alignSelf: "flex-start", border: 0, background: "transparent", color: "#2563EB",
        fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: 0,
      }}>View details →</button>
    </div>
  );
}
function Sep() {
  return <span style={{ width: 1, height: 14, background: ig.borderSoft, margin: "0 2px" }} />;
}

/* ─────────────────────────────────────────────────────────────
   Styles
   ───────────────────────────────────────────────────────────── */
const input = {
  width: "100%", height: 36, padding: "0 11px", borderRadius: 9,
  border: `1px solid ${ig.border}`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink,
};
const btnSecondary = {
  padding: "9px 14px", borderRadius: 9, border: `1px solid ${ig.border}`,
  background: ig.card, color: ig.ink, fontWeight: 700, fontSize: 12.5, cursor: "pointer",
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 9,
  border: 0, background: "linear-gradient(135deg, #18B86B, #22C55E)", color: "#fff",
  fontWeight: 800, fontSize: 12.5, cursor: "pointer",
  boxShadow: "0 4px 12px rgba(24,184,107,0.28)",
};
const iconBtnGhost = {
  width: 28, height: 28, borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card,
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.ink, padding: 0,
};
const chipBtn = {
  display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999,
  border: `1px solid ${ig.border}`, background: ig.card, color: ig.muted, fontSize: 11.5,
  fontWeight: 700, cursor: "pointer",
};
const modeCard = {
  border: `1.5px solid ${ig.border}`, borderRadius: 10, padding: "12px 12px", background: ig.card,
  cursor: "pointer", textAlign: "left",
};
const miniTool = {
  width: 26, height: 26, borderRadius: 6, border: 0, background: "transparent",
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.muted, padding: 0,
};
const selectMini = {
  height: 26, padding: "0 6px", borderRadius: 6, border: `1px solid ${ig.borderSoft}`,
  background: ig.card, fontSize: 11.5, color: ig.ink, outline: "none",
};
const aiBtn = {
  display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 999,
  border: "1px solid #DDD6FE", background: "#F5F3FF", color: "#6D28D9",
  fontSize: 11.5, fontWeight: 800, cursor: "pointer",
};
const meta = { fontSize: 11, color: ig.muted, fontWeight: 600 };
const dot  = { width: 3, height: 3, borderRadius: 999, background: "#CBD5D0" };
const cd   = { background: "#EEF2F0", padding: "1px 5px", borderRadius: 4, fontFamily: "JetBrains Mono, monospace", fontSize: 10.5 };

const FIELDS_BY_TYPE = {
  send_whatsapp: [
    { key: "wa_sender",    label: "WhatsApp sender",  placeholder: "+1 555 …" },
    { key: "message_type", label: "Message type", type: "select", options: ["text", "template", "media", "interactive"], default: "text" },
    { key: "body",         label: "Body", type: "textarea" },
  ],
  wait: [
    { key: "wait_type", label: "Wait type", type: "select", options: ["fixed_time", "until_business_hours", "until_reply", "until_appointment"], default: "fixed_time" },
    { key: "duration",  label: "Duration", type: "number", placeholder: "5" },
    { key: "unit",      label: "Unit", type: "select", options: ["minutes", "hours", "days", "weeks"], default: "minutes" },
  ],
  if_else: [
    { key: "field",    label: "Field", type: "select",
      options: ["funding_readiness_status", "appointment_status", "tags", "credit_score_range",
                "monthly_income", "language", "ai_intent", "ticket_status"] },
    { key: "operator", label: "Operator", type: "select",
      options: ["equals", "not_equals", "contains", "greater_than", "less_than", "is_empty", "is_not_empty"] },
    { key: "value",    label: "Value", placeholder: "Ready to Apply" },
  ],
  add_tag:    [{ key: "tag", label: "Tag to add", placeholder: "Funding Lead" }],
  remove_tag: [{ key: "tag", label: "Tag to remove" }],
  update_contact_field: [
    { key: "field", label: "Field", placeholder: "funding_readiness_status" },
    { key: "value", label: "New value", placeholder: "Needs Credit Review" },
  ],
  create_opportunity: [
    { key: "pipeline", label: "Pipeline",  placeholder: "Sales" },
    { key: "stage",    label: "Stage",     placeholder: "New Lead" },
    { key: "value",    label: "Deal value", type: "number" },
    { key: "owner",    label: "Owner" },
  ],
  book_appointment: [
    { key: "calendar",         label: "Calendar" },
    { key: "assigned_user",    label: "Assigned user" },
    { key: "duration_minutes", label: "Duration (min)", type: "number" },
  ],
  create_ticket: [
    { key: "title",       label: "Ticket title" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category",    label: "Category", type: "select",
      options: ["Funding", "Credit Repair", "Billing", "Technical Support", "Document Upload", "Application Review", "Appointment", "General Question"] },
    { key: "priority",    label: "Priority", type: "select", options: ["low", "medium", "high", "urgent"], default: "medium" },
    { key: "assigned_to", label: "Assigned to" },
  ],
  webhook: [
    { key: "url",     label: "URL", placeholder: "https://api.example.com/hook" },
    { key: "method",  label: "Method", type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"], default: "POST" },
    { key: "headers", label: "Headers (JSON)", type: "textarea" },
    { key: "body",    label: "Body", type: "textarea" },
  ],
  ai_decision_maker: [
    { key: "input_source", label: "Input source", placeholder: "Conversation history + contact fields" },
    { key: "instruction",  label: "Instruction for AI", type: "textarea",
      placeholder: "Decide which branch to take based on funding readiness and last reply." },
    { key: "confidence_threshold", label: "Confidence threshold", type: "number", placeholder: "0.7" },
  ],
  __default: [
    { key: "notes", label: "Configuration notes", type: "textarea",
      placeholder: "This step's detailed configuration ships with the matching integration in the next phase." },
  ],
};
